if (!customElements.get('cart-terms')) {
  // Shared by every send-in terms box (drawer and cart page), see saveAcceptance
  let sendinSaveQueue = Promise.resolve();

  class CartTerms extends HTMLElement {
    constructor() {
      super();
      this.cartTermsCheckbox = this.querySelector('.js-cart-terms-checkbox');
      this.form = document.getElementById(this.cartTermsCheckbox.getAttribute('form'));
      this.submitHandler = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.submitHandler);
      this.initSendinTerms();
    }

    disconnectedCallback() {
      this.form.removeEventListener('submit', this.submitHandler);
    }

    /**
     * Send-in terms (snippets/sendin-form-terms.liquid): the "Send-In Terms & Conditions" link opens a popup
     * with the terms, "I accept these terms" in the popup ticks the box, and checkout without the tick shows
     * an inline message under the box instead of a browser alert.
     */
    initSendinTerms() {
      this.sendinBox = this.querySelector('[data-sendin-terms-box]');
      if (!this.sendinBox) return;
      this.sendinError = this.querySelector('[data-sendin-terms-error]');
      const dialog = this.querySelector('[data-sendin-terms-dialog]');

      this.cartTermsCheckbox.addEventListener('change', () => {
        if (this.cartTermsCheckbox.checked) this.setInvalid(false);
        this.saveAcceptance();
      });

      if (!dialog) return;
      this.querySelector('[data-sendin-terms-open]').addEventListener('click', (evt) => {
        evt.preventDefault();
        dialog.showModal();
      });
      this.querySelector('[data-sendin-terms-close]').addEventListener('click', () => dialog.close());
      this.querySelector('[data-sendin-terms-accept]').addEventListener('click', () => {
        dialog.close();
        if (!this.cartTermsCheckbox.checked) {
          this.cartTermsCheckbox.checked = true;
          this.cartTermsCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.cartTermsCheckbox.focus();
      });
      // A click on the backdrop lands on the dialog element itself
      dialog.addEventListener('click', (evt) => {
        if (evt.target === dialog) dialog.close();
      });
      // Escape closes only the popup, not the cart drawer behind it. The popup closes on keydown and hands
      // focus back to the link, so the keyup that closes the drawer arrives from outside the popup: stop it here.
      dialog.addEventListener('cancel', () => {
        this.popupClosedByEscape = true;
        setTimeout(() => {
          this.popupClosedByEscape = false;
        }, 500);
      });
      this.addEventListener('keyup', (evt) => {
        if (evt.key !== 'Escape') return;
        if (dialog.open || this.popupClosedByEscape) evt.stopPropagation();
        this.popupClosedByEscape = false;
      });
    }

    /**
     * Shows or clears the "please tick the box" state of the send-in terms.
     * @param {boolean} invalid - Whether checkout was attempted without the tick.
     */
    setInvalid(invalid) {
      this.sendinBox.classList.toggle('is-invalid', invalid);
      if (this.sendinError) this.sendinError.hidden = !invalid;
    }

    /**
     * Saves the tick on the cart right away, so the drawer and the cart page both render it
     * ticked from then on and the customer only accepts once.
     */
    saveAcceptance() {
      const attribute = this.cartTermsCheckbox.dataset.termsAttribute;
      if (!attribute) return;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      // One save at a time across the whole cart, each sending the box's state when it runs, so quick
      // tick/untick clicks cannot finish out of order. A cart refresh replaces this element; a save still
      // waiting from the replaced copy is skipped, because the new copy saves its own state.
      sendinSaveQueue = sendinSaveQueue
        .then(() => {
          if (!this.isConnected) return undefined;
          return fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { [attribute]: this.cartTermsCheckbox.checked ? 'Yes' : '' } }),
          });
        })
        .catch(() => {
          // The checkbox still submits with the cart form at checkout
        });
    }

    /**
     * Handles cart form submit
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (this.cartTermsCheckbox.checked) return;
      evt.preventDefault();
      if (this.sendinBox) {
        this.setInvalid(true);
        this.sendinBox.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        this.cartTermsCheckbox.focus({ preventScroll: true });
        return;
      }
      alert(theme.strings.cartTermsConfirmation); // eslint-disable-line
    }
  }
  customElements.define('cart-terms', CartTerms);
}
